import { IconButton } from '@plato/ui/candidates/icon-button';
import { Button } from '@plato/ui/primitives/button';
import type { SerializedEditorState } from 'lexical';
import { ArrowRight, CheckIcon, RefreshCcw, ThumbsDownIcon, ThumbsUpIcon, XIcon } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { PulseLoader } from 'react-spinners';
import { v4 as uuidv4 } from 'uuid';
import { AiIcon } from '../../../assets/icons/ai-icon.tsx';
import { Text } from '../../../components/ui/text.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { languageConfigs } from '../../../lib/i18n/language-config.ts';
import { getTextContent } from '../../../lib/rich-text/get-text-content.ts';
import type { SuggestedText } from '../../../server/queries/improve-writing-suggest-query.ts';
import { api } from '../../../utils/api.ts';
import { useAiFeedback } from '../../ai-feedback/hooks/use-ai-feedback.ts';
import type { AiFeedbackType } from '../../ai-feedback/types/ai-feedback-types.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { DialogStateManager, useDialog } from '../../dialog/dialog-provider.tsx';
import Dialog from '../../dialog/dialog.tsx';
import type { ModalInnerProps } from '../../modal/index.tsx';
import { RichTextViewer } from '../../rich-text/components/rich-text-viewer.tsx';
import { suggestedTextToLexical } from '../lib/suggested-text-to-lexical.ts';

export type ImproveTextDialogProps = {
    text: SerializedEditorState;
    onConfirmation: (text: SerializedEditorState, sessionId: string) => void;
    entityId?: string;
} & ModalInnerProps;

export const ImproveTextDialog = DialogStateManager.create<ImproveTextDialogProps>(
    ({ text, onConfirmation: onConfirmationProp, entityId }): ReactNode => {
        const { visible, remove } = useDialog();
        const { t, language } = useLocale();
        const [hasRated, setHasRated] = useState(false);
        const suggestMutation = api.platoUser.command.improveWriting.suggest.useMutation();
        const { sendFeedback } = useAiFeedback();
        const sendEvent = useAnalytics();
        const sessionIdRef = useRef<string>(uuidv4());
        const hasRequestedRef = useRef(false);
        const hasAcceptedRef = useRef(false);

        const analyticsPayload = {
            entity_type: 'note' as const,
            entity_id: entityId,
            session_id: sessionIdRef.current,
        };

        const sendFeedbackForCurrentSuggestion = (feedbackType: AiFeedbackType): void => {
            void sendFeedback({
                feedbackType,
                sessionId: sessionIdRef.current,
                entityType: 'note',
                payload: {
                    entityId,
                    metadata: {
                        ...suggestMutation.data?.metadata,
                        id: suggestMutation.data?.id ?? '',
                    },
                },
            }).catch(() => {
                // ignore feedback errors to avoid impacting UX
            });
        };

        const handleRate = (rating: Extract<AiFeedbackType, 'thumbs_up' | 'thumbs_down'>): void => {
            setHasRated(true);
            sendFeedbackForCurrentSuggestion(rating);
        };

        const improveText = (): void => {
            const plainText = getTextContent(text);

            if (!plainText) {
                return;
            }

            if (hasRequestedRef.current) {
                sendEvent('ai_text_regenerate_formatting', analyticsPayload);
                sendFeedbackForCurrentSuggestion('regenerate');
            } else {
                sendEvent('ai_text_request_formatting', analyticsPayload);
                hasRequestedRef.current = true;
            }

            setHasRated(false);

            suggestMutation.mutate({
                entityType: 'note',
                entityId,
                text: plainText,
                outputLanguage: languageConfigs[language].aiOutputLanguageName,
            });
        };

        useEffect(() => {
            improveText();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        const convertSuggestedTextToLexical = (text: SuggestedText): SerializedEditorState | null => {
            return suggestedTextToLexical(text, {
                summary: t('rich_text_editor__heading_summary'),
                opportunities: t('rich_text_editor__heading_opportunities'),
                feedback: t('rich_text_editor__heading_feedback'),
                nextSteps: t('rich_text_editor__heading_next_steps'),
            });
        };

        const confirm = (): void => {
            if (!suggestMutation.data) {
                return;
            }

            const converted = convertSuggestedTextToLexical(suggestMutation.data);

            sendEvent('ai_text_accept_formatting', analyticsPayload);
            sendFeedbackForCurrentSuggestion('accept');
            hasAcceptedRef.current = true;

            onConfirmationProp(converted ?? text, sessionIdRef.current);

            remove();
        };

        const cancel = (): void => {
            if (suggestMutation.data && !hasAcceptedRef.current) {
                sendEvent('ai_text_reject_formatting', analyticsPayload);
                sendFeedbackForCurrentSuggestion('reject');
            }
            remove();
        };

        return (
            <Dialog open={visible} onClose={cancel}>
                <div className="flex max-sm:h-full w-screen max-w-md flex-col gap-5 px-5 py-5 sm:px-6">
                    <div className="flex items-center justify-between gap-3">
                        <Text size="lg" weight="medium" color="neutral-900">
                            {t('rich_text_editor__improve_writing')}
                        </Text>
                        <IconButton
                            variant="secondary"
                            label={t('form__cancel', { ns: 'common' })}
                            onClick={cancel}
                            className="h-auto min-w-0 rounded-md bg-transparent p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
                        >
                            <XIcon className="size-5" />
                        </IconButton>
                    </div>
                    <div className="max-sm:flex-1 max-sm:min-h-0 max-sm:overflow-y-auto">
                        <RichTextViewer
                            content={
                                suggestMutation.data
                                    ? (convertSuggestedTextToLexical(suggestMutation.data) ?? text)
                                    : text
                            }
                        />
                    </div>
                    <div className="flex gap-2 items-center">
                        {suggestMutation.isPending ? (
                            <>
                                <AiIcon />
                                <div className="flex items-baseline">
                                    <Text variant="ai">{t('rich_text_editor__improve_writing_loading')}</Text>
                                    <PulseLoader size={3} color="#8D54E9" />
                                </div>
                            </>
                        ) : hasRated ? (
                            <>
                                <CheckIcon className="w-4 h-4 text-green-600" />
                                <Text size="xs">{t('rich_text_editor__feedback_thank_you')}</Text>
                            </>
                        ) : (
                            <>
                                <Text size="xs">{t('rich_text_editor__rate_suggestion')}</Text>
                                <IconButton
                                    variant="secondary"
                                    label={t('rich_text_editor__thumbs_up')}
                                    data-level="2"
                                    onClick={() => {
                                        handleRate('thumbs_up');
                                    }}
                                >
                                    <ThumbsUpIcon />
                                </IconButton>
                                <IconButton
                                    variant="secondary"
                                    label={t('rich_text_editor__thumbs_down')}
                                    data-level="2"
                                    onClick={() => {
                                        handleRate('thumbs_down');
                                    }}
                                >
                                    <ThumbsDownIcon />
                                </IconButton>
                            </>
                        )}
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button
                            variant="secondary"
                            onClick={improveText}
                            disabled={suggestMutation.isPending}
                        >
                            <RefreshCcw />
                            {t('rich_text_editor__alternative')}
                        </Button>
                        <Button variant="primary" onClick={confirm} disabled={suggestMutation.isPending}>
                            <ArrowRight />
                            {t('rich_text_editor__insert')}
                        </Button>
                    </div>
                </div>
            </Dialog>
        );
    },
);
